import { useState } from "react";
import { DEFAULT_TABLE_COLUMNS } from "./constants";
import { computeRow } from "./utils";

export const HeaderBar = ({
  id,
  meta,
  alignMode,
  onRename,
  onToggleLock,
  onRemove,
}) => (
  <div className="select-none flex items-center justify-between px-3 py-2.5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
    <div className="grab-handle flex items-center gap-2 cursor-move">
      <EditableTitle
        disabled={alignMode}
        title={meta.title}
        onChange={(t) => onRename(id, t)}
      />
      <div className="text-xs text-slate-400">드래그 핸들</div>
    </div>
    <div className="flex items-center gap-1.5">
      <Btn
        onClick={() => onToggleLock(id)}
        label={meta.locked ? "잠금 해제" : "잠금"}
      />
      {/* 파일 블록은 삭제 버튼 숨김 / 접기·복제 기능 없음 */}
      {["custom", "table"].includes(meta.kind) && (
        <Btn onClick={() => onRemove(id)} label="삭제" />
      )}
    </div>
  </div>
);

export const EditableTitle = ({ title, onChange, disabled }) => {
  const [editing, setEditing] = useState(false);
  return editing && !disabled ? (
    <input
      autoFocus
      defaultValue={title}
      aria-label="칸 이름 입력"
      onBlur={(e) => {
        onChange(e.target.value.trim() || title);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") setEditing(false);
      }}
      className="text-sm font-semibold px-2 py-1 rounded-md border border-slate-200 outline-none no-drag"
    />
  ) : (
    <div
      className="text-sm font-semibold text-slate-700 cursor-text no-drag"
      title="클릭하여 이름 편집"
      onClick={() => !disabled && setEditing(true)}
    >
      {title}
    </div>
  );
};

export const Btn = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-2 py-1 text-xs rounded-md border border-slate-200 bg-white hover:bg-slate-50 no-drag"
  >
    {label}
  </button>
);

export const FilesBlock = ({ files, onAttach, onRemove, alignMode }) => (
  <div className="p-3">
    <div className="flex items-center gap-2">
      <input
        type="file"
        multiple
        onChange={onAttach}
        disabled={alignMode}
        className="disabled:opacity-50 no-drag"
      />
      <span className="text-xs text-slate-500">여러 파일 선택 가능</span>
    </div>
    <ul className="mt-2 space-y-2">
      {files.map((f) => (
        <li
          key={f.id}
          className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{f.name}</div>
            <div className="text-xs text-slate-500">
              {(f.size / 1024).toFixed(1)} KB · {f.type || "file"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={f.dataUrl}
              download={f.name}
              className="px-2 py-1 text-xs rounded-md border bg-white"
            >
              다운로드
            </a>
            {!alignMode && (
              <button
                type="button"
                onClick={() => onRemove(f.id)}
                className="px-2 py-1 text-xs rounded-md border bg-white"
              >
                삭제
              </button>
            )}
          </div>
        </li>
      ))}
      {files.length === 0 && (
        <li className="text-xs text-slate-400">아직 첨부된 파일이 없습니다.</li>
      )}
    </ul>
  </div>
);

export const TableBlock = ({
  bid,
  t,
  alignMode,
  onAddRow,
  onRemoveRow,
  onUpdateCell,
}) => {
  const cols = t.columns || DEFAULT_TABLE_COLUMNS;
  const rows = (t.rows || []).map((r) => computeRow(r, cols));
  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="flex-1 min-h-0 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">표 항목</div>
        {!alignMode && (
          <button
            className="px-3 py-1.5 rounded-lg border bg-white"
            onClick={() => onAddRow(bid)}
            type="button"
          >
            행 추가
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="px-2 py-2 text-left"
                  style={{ width: c.width || "auto" }}
                >
                  {c.label}
                </th>
              ))}
              <th className="px-2 py-2 w-[6%]"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="border-t">
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={`px-2 py-1.5 ${
                      c.type === "number" || c.type === "calc"
                        ? "text-right"
                        : ""
                    }`}
                  >
                    {c.type === "calc" ? (
                      <div className="min-w-[80px]">
                        {Number(r[c.key] || 0).toLocaleString()}
                      </div>
                    ) : c.type === "number" ? (
                      <input
                        type="number"
                        className="w-full rounded border px-2 py-1 text-right"
                        value={r[c.key] ?? 0}
                        onChange={(e) =>
                          onUpdateCell(bid, r._id, c.key, e.target.value)
                        }
                        disabled={alignMode}
                      />
                    ) : (
                      <input
                        className="w-full rounded border px-2 py-1"
                        value={r[c.key] ?? ""}
                        onChange={(e) =>
                          onUpdateCell(bid, r._id, c.key, e.target.value)
                        }
                        disabled={alignMode}
                      />
                    )}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right">
                  {!alignMode && (
                    <button
                      className="px-2 py-1 rounded border text-xs"
                      onClick={() => onRemoveRow(bid, r._id)}
                      type="button"
                    >
                      삭제
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={cols.length + 1}
                  className="px-3 py-4 text-center text-gray-400"
                >
                  행이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan={cols.findIndex((c) => c.key === "qty")} />
              <td className="px-2 py-2 text-right font-medium">
                {totalQty.toLocaleString()}
              </td>
              <td className="px-2 py-2 text-right font-medium">
                {totalAmount.toLocaleString()}
              </td>
              <td
                colSpan={Math.max(
                  0,
                  cols.length - (cols.findIndex((c) => c.key === "qty") + 3)
                )}
              ></td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
